import React, { useCallback, useMemo } from "react";
import { StyleSheet, Animated, View, Pressable } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import CustomText from "../../common/general/CustomText/CustomText";
import { useMarkNotificationAsReadMutation, type Notification } from "../../../graphql/generated/graphql";

import { type AnnouncementGroup, getGroupFromCreatedDate, announcementGroups } from "../../../utils/notificationHelper";
import { formatDateWithTime } from "../../../utils/date";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import { NotificationServiceResult } from "../../../../backend/src/graphql/schema/notification/notification.service";

type UserNotification = Pick<Notification, "id" | "isRead" | "title" | "message" | "created">;

function NotificationsSlideIn({
  visible,
  slideAnim,
  onClose,
  notifications = [],
  loading = true,
  refetch,
}: {
  visible: boolean;
  slideAnim: any;
  onClose: () => void;
  notifications?: UserNotification[];
  loading?: boolean;
  refetch: () => void;
}) {
  if (!visible) return null;

  const [markNotificationAsSeen] = useMarkNotificationAsReadMutation();

  const notificationsByGroups = useMemo(() => {
    const groups: { [key in AnnouncementGroup]?: UserNotification[] } = {};
    const sortedAnnouncements = [...notifications].sort(
      (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime()
    );
    for (const announcement of sortedAnnouncements) {
      const category = getGroupFromCreatedDate(new Date(announcement.created).getTime());
      if (groups[category] == null) {
        groups[category] = [];
      }
      groups[category].push(announcement);
    }

    return groups;
  }, [notifications]);

  const handleMarkNotificationAsSeen = useCallback(async (id: string) => {
    const result = await markNotificationAsSeen({
      variables: {
        id,
      },
    });
    refetch();
  }, []);

  return (
    <Animated.View style={[styles.mobile, { transform: [{ translateX: slideAnim }] }]}>
      <View style={styles.mobileContainer}>
        <View style={styles.topRow}>
          <CustomText
            size="m"
            weight="bold"
            useTranslationText={false}
            style={styles.headerText}
            text="Notifications"
          />
          <Pressable role="button" aria-label="Close notifications" style={styles.closeButton} onPress={onClose}>
            <Ionicons name="close" size={24} color="black" />
          </Pressable>
        </View>

        <View style={styles.contentContainer}>
          {announcementGroups.map((category) => {
            const groupNotifications = notificationsByGroups[category as AnnouncementGroup];
            if (groupNotifications == null) {
              return null;
            }
            return (
              <View key={category} style={styles.notificationsCategory}>
                <View style={styles.categoryTitleRow}>
                  <CustomText
                    size="m"
                    weight="normal"
                    useTranslationText={true}
                    style={styles.categoryTitle}
                    text={category}
                  />
                </View>
                <View style={styles.notificationsList}>
                  {groupNotifications.map((notification, index) => (
                    <View
                      key={notification.id}
                      style={[styles.notificationItem, !notification.isRead && styles.unreadNotification]}
                      onPointerDown={() => {
                        handleMarkNotificationAsSeen(notification.id);
                      }}>
                      <View style={styles.notificationReadIndicator}>
                        {!notification.isRead && <View style={styles.unreadDot} />}
                      </View>
                      <View style={styles.notificationContent}>
                        <View style={styles.notificationTitleRow}>
                          <CustomText
                            size="s"
                            weight="bold"
                            useTranslationText={false}
                            style={styles.notificationTitle}
                            text={notification.title}
                          />
                          <CustomText
                            size="xs"
                            weight="normal"
                            useTranslationText={false}
                            style={styles.timestamp}
                            text={formatDateWithTime(notification.created)}
                          />
                        </View>
                        <CustomText
                          size="s"
                          weight="normal"
                          useTranslationText={false}
                          style={styles.notificationMessage}
                          text={notification.message}
                        />
                      </View>
                    </View>
                  ))}
                </View>
              </View>
            );
          })}
          {notifications.length === 0 && (
            <View style={styles.emptyContainer}>
              <Ionicons name="notifications-outline" size={48} color="#9CA3AF" />
              <CustomText
                size="m"
                weight="medium"
                useTranslationText={false}
                style={styles.emptyText}
                text="No notifications yet"
              />
              <CustomText
                size="s"
                weight="normal"
                useTranslationText={false}
                style={styles.emptySubtext}
                text="We'll notify you when something important happens"
              />
            </View>
          )}
        </View>
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  mobile: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: 360,
    zIndex: 100,
    backgroundColor: "#fff",
    height: "100vh",
  },
  mobileContainer: {
    flex: 1,
    backgroundColor: "white",
    borderLeftWidth: 1,
    borderLeftColor: "#e0e0e0",
  },
  topRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  headerText: {
    fontSize: 18,
  },
  closeButton: {
    padding: 5,
  },
  contentContainer: {
    flex: 1,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingBottom: 100,
  },
  emptyText: {
    marginTop: 16,
    color: "#4B5563",
  },
  emptySubtext: {
    marginTop: 8,
    color: "#9CA3AF",
    textAlign: "center",
  },
  notificationsList: {
    flex: 1,
  },
  notificationItem: {
    paddingVertical: 12,
    flexDirection: "row",
    gap: 0,
    backgroundColor: "white",
  },
  unreadNotification: {
    backgroundColor: "#F9FAFB",
    cursor: "pointer",
  },
  notificationTitleRow: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  notificationTitle: {
    flex: 1,
    fontSize: 16,
    lineHeight: 24,
    color: "#111827",
  },
  notificationMessage: {
    color: "#475467",
  },
  timestamp: {
    color: "#9CA3AF",
  },
  notificationsCategory: {
    marginBottom: 10,
  },
  categoryTitleRow: {
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  categoryTitle: {
    color: "#667085",
    fontWeight: 600,
    fontSize: 14,
    lineHeight: 20,
  },
  notificationReadIndicator: {
    width: 40,
    alignItems: "center",
  },
  unreadDot: {
    borderRadius: 11,
    width: 11,
    height: 11,
    backgroundColor: "#0084FF",
    marginTop: 7,
  },
  notificationContent: {
    flex: 1,
    marginRight: 16,
    gap: 8,
  },
});

export default NotificationsSlideIn;
